/**
 * RyElement - Base class for all ry-ui components
 *
 * Provides:
 * - Lifecycle hooks with auto-cleanup
 * - Event helper methods
 * - State management via data attributes
 * - DOM query helpers
 * - Shared overlay helpers (outside-click, escape, focus return, scroll lock, portal)
 */

import type { CleanupFn, StateChangeDetail } from '../types.js';

// Module-level state for reference-counted body scroll lock. Multiple overlays
// may open simultaneously (modal opens a dropdown opens a tooltip); they all
// share the same body. Naive set-and-remove leaks scroll-lock when the inner
// overlay closes first. Reference count fixes the documented nested-overlay
// scroll-lock clobber bug.
let scrollLockCount = 0;
let scrollLockSavedPaddingRight: string | null = null;

export class RyElement extends HTMLElement {
  // Store for cleanup functions
  #cleanupFns: CleanupFn[] = [];

  // Store for MutationObserver
  #observer: MutationObserver | null = null;

  constructor() {
    super();
  }

  /**
   * Called when element is added to DOM.
   * Override setup() in subclass instead of this.
   */
  connectedCallback(): void {
    // Mark as initialized
    this.setAttribute('data-ry-initialized', '');

    // Call subclass setup
    this.setup?.();
  }

  /**
   * Called when element is removed from DOM.
   * Automatically cleans up event listeners and observers.
   */
  disconnectedCallback(): void {
    // Run all cleanup functions
    this.#cleanupFns.forEach(fn => fn());
    this.#cleanupFns = [];

    // Disconnect observer
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    // Call subclass teardown
    this.teardown?.();
  }

  /**
   * Override in subclass for component initialization
   */
  setup?(): void;

  /**
   * Override in subclass for cleanup
   */
  teardown?(): void;

  /**
   * Add event listener with automatic cleanup on disconnect.
   */
  on<K extends keyof HTMLElementEventMap>(
    target: EventTarget,
    event: K,
    handler: (e: HTMLElementEventMap[K]) => void,
    options?: AddEventListenerOptions
  ): (e: HTMLElementEventMap[K]) => void;

  on(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): EventListener;

  on(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options: AddEventListenerOptions = {}
  ): EventListener {
    const boundHandler = handler.bind(this) as EventListener;
    target.addEventListener(event, boundHandler, options);

    this.#cleanupFns.push(() => {
      target.removeEventListener(event, boundHandler, options);
    });

    return boundHandler;
  }

  /**
   * Emit a custom event with ry: prefix.
   */
  emit<T = void>(name: string, detail?: T, options: CustomEventInit = {}): boolean {
    const event = new CustomEvent(`ry:${name}`, {
      bubbles: true,
      cancelable: true,
      detail,
      ...options
    });
    return this.dispatchEvent(event);
  }

  /**
   * Get/set component state via data attribute.
   */
  get state(): string {
    return this.dataset.ryState ?? 'closed';
  }

  set state(value: string) {
    const oldState = this.state;
    if (oldState !== value) {
      this.dataset.ryState = value;
      this.emit<StateChangeDetail>('state-change', { oldState, newState: value });
    }
  }

  /**
   * Query within this element.
   */
  $<E extends Element = Element>(selector: string): E | null {
    return this.querySelector<E>(selector);
  }

  /**
   * Query all within this element.
   */
  $$<E extends Element = Element>(selector: string): E[] {
    return [...this.querySelectorAll<E>(selector)];
  }

  /**
   * Setup a MutationObserver on this element.
   */
  observe(
    callback: MutationCallback,
    options: MutationObserverInit = { childList: true, subtree: true }
  ): void {
    if (this.#observer) {
      this.#observer.disconnect();
    }

    this.#observer = new MutationObserver(callback.bind(this));
    this.#observer.observe(this, options);
  }

  /**
   * Wait for next animation frame.
   */
  nextFrame(): Promise<number> {
    return new Promise(resolve => requestAnimationFrame(resolve));
  }

  /**
   * Trap focus within a container (for modals, dropdowns).
   */
  trapFocus(container: HTMLElement): void {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusableEls = container.querySelectorAll<HTMLElement>(focusableSelectors);
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    const handleKeydown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl?.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl?.focus();
        }
      }
    };

    this.on(container, 'keydown', handleKeydown);

    // Focus first element
    firstEl?.focus();
  }

  // ─── Shared overlay helpers ────────────────────────────────────────

  /**
   * Listen for clicks outside this element. Calls `handler` when a document
   * click occurs and the target is NOT a descendant of `this`. The listener
   * auto-removes on disconnect (uses `on()` internally).
   *
   * @param handler  Called with the original MouseEvent.
   * @param opts.skip  If provided and returns true, the handler is NOT called for that click. Use for skipping when overlay is in a non-open state, when [persistent], etc.
   * @param opts.ignore  Optional list of additional elements to treat as "inside" — clicks on these don't fire the handler. Useful when the trigger is not a descendant (e.g. modal with external `<ry-button modal="id">`).
   *
   * @returns A cleanup function. Calling it removes the listener early.
   */
  onOutsideClick(
    handler: (e: MouseEvent) => void,
    opts: { skip?: () => boolean; ignore?: Iterable<EventTarget | null> } = {},
  ): CleanupFn {
    const onClick = (e: Event): void => {
      if (opts.skip?.()) return;
      const t = e.target as Node | null;
      if (t && this.contains(t)) return;
      if (opts.ignore) {
        for (const el of opts.ignore) {
          if (el && (el === t || (el instanceof Node && el.contains(t)))) return;
        }
      }
      handler(e as MouseEvent);
    };
    // `on()` binds the handler and returns the bound copy — we must remove
    // the SAME bound reference, not the original closure.
    const bound = this.on(document, 'click', onClick);
    return () => document.removeEventListener('click', bound);
  }

  /**
   * Listen for Escape keydown. Calls `handler` when Escape is pressed and
   * `skip()` doesn't return true. Listener auto-removes on disconnect.
   *
   * @param handler  Called with the original KeyboardEvent.
   * @param opts.skip  If provided and returns true, the handler is NOT called. Use for `[persistent]` overlays or when state !== 'open'.
   *
   * @returns A cleanup function.
   */
  onEscape(
    handler: (e: KeyboardEvent) => void,
    opts: { skip?: () => boolean } = {},
  ): CleanupFn {
    const onKey = (e: Event): void => {
      const ke = e as KeyboardEvent;
      if (ke.key !== 'Escape') return;
      if (opts.skip?.()) return;
      handler(ke);
    };
    const bound = this.on(document, 'keydown', onKey);
    return () => document.removeEventListener('keydown', bound);
  }

  /**
   * Capture the currently-focused element NOW. Returns a function that
   * restores focus to it. Use when opening an overlay so the user lands
   * back where they were after close. Replaces the hand-rolled
   * `#previouslyFocused = document.activeElement` pattern in modal/drawer.
   *
   * The restore function is safe to call even if the originally-focused
   * element has been removed from the DOM (it's a no-op in that case).
   *
   * @returns A function that restores focus.
   */
  withReturnFocus(): () => void {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    return () => {
      if (previouslyFocused && document.contains(previouslyFocused) && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }

  /**
   * Lock body scroll. Reference-counted — calling multiple times stacks; the
   * lock is released only when `unlockScroll()` has been called the same
   * number of times. Compensates for scrollbar width by setting
   * `padding-right` so layout doesn't shift when the scrollbar disappears.
   *
   * Sets `data-ry-scroll-lock` on `<body>` for CSS hooks (e.g. backdrop
   * components that want to know body is locked).
   */
  lockScroll(): void {
    // Defensive sync: if the body attribute is missing but our counter > 0,
    // some previous lock leaked (e.g. test environment cleaned body between
    // tests, or a component disconnected without close()). Reset to consistent.
    if (scrollLockCount > 0 && !document.body.hasAttribute('data-ry-scroll-lock')) {
      scrollLockCount = 0;
    }
    scrollLockCount++;
    if (scrollLockCount > 1) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    scrollLockSavedPaddingRight = document.body.style.paddingRight || '';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.setAttribute('data-ry-scroll-lock', '');
  }

  /**
   * Release one reference of the body scroll lock. The lock is fully
   * released only when the count reaches zero.
   */
  unlockScroll(): void {
    if (scrollLockCount === 0) return;
    scrollLockCount--;
    if (scrollLockCount > 0) return;
    document.body.style.paddingRight = scrollLockSavedPaddingRight ?? '';
    scrollLockSavedPaddingRight = null;
    document.body.removeAttribute('data-ry-scroll-lock');
  }

  /**
   * Move `el` to `<body>` so it escapes any ancestor stacking context or
   * `overflow: hidden`. Returns a function that restores the element to its
   * original parent + position.
   *
   * Use for the floating element of `<ry-popover>` (and components that
   * compose from it) on open, restore on close.
   *
   * @param el  The element to portal. Typically a child of the host
   *            component, NOT the host itself (the host stays put so slot
   *            semantics and event listeners remain natural).
   * @returns A function that restores the element.
   */
  portalToBody(el: HTMLElement): () => void {
    const originalParent = el.parentNode;
    const originalNextSibling = el.nextSibling;
    document.body.appendChild(el);
    return () => {
      if (!originalParent) {
        el.remove();
        return;
      }
      if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
        originalParent.insertBefore(el, originalNextSibling);
      } else {
        originalParent.appendChild(el);
      }
    };
  }
}
